/**
 * GenerateModal — 통합 사진 생성 Modal
 *
 * 1. 촬영 선택 (기존/새 촬영)
 * 2. 역할 선택 (신랑/신부/커플)
 * 3. 참조 사진 선택
 * 4. 컨셉 선택
 * 5. 생성 개수 (1~10)
 * 6. 항상 SSE 실시간 생성, 모달 닫기/이탈 시 서버가 백그라운드 완료
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, Button, Input } from '@/components/ui'
import type { PromptBlockSelection } from '@cugumi/shared/schemas'
import { usePromptBlocksGrouped } from '@/hooks/usePromptBlocks'
import { useSSEGenerate } from '@/hooks/useSSEGenerate'
import { useAlbums } from '@/hooks/useAlbums'
import { useUIStore } from '@/stores/uiStore'
import { useBackgroundGenStore } from '@/stores/backgroundGenStore'
import { useGenerationForm } from '../hooks/useGenerationForm'
import { RoleSelector } from './RoleSelector'
import { ReferencePhotoSelector } from './ReferencePhotoSelector'
import { ConceptSelector } from './ConceptSelector'
import { GenerationProgress } from './GenerationProgress'
import styles from './DrawerForm.module.css'

interface GenerateModalProps {
  open: boolean
  onClose: () => void
  preselectedAlbumId?: string
}

type SSEEvent =
  | { type: 'status'; message: string }
  | { type: 'image'; index: number; url: string; progress: number; total: number }
  | { type: 'done'; generationId: string; urls: string[]; remainingCredits: number; creditsUsed: number; successCount: number; failureCount: number; modelId: string; prompt: string; albumId?: string }
  | { type: 'error'; error: string }

const GENERATIONS_QUERY_KEY = ['/api/ai/generations?pageSize=8']

type GenerationsCache = { data: { generations: Array<{ id: string; generatedUrls: string[]; style: string | null; createdAt: string }> } }

function updateGenerationsCache(
  queryClient: ReturnType<typeof useQueryClient>,
  updater: (generations: GenerationsCache['data']['generations']) => GenerationsCache['data']['generations'],
) {
  queryClient.setQueryData(GENERATIONS_QUERY_KEY, (old: unknown) => {
    if (!old || typeof old !== 'object' || !('data' in old)) return old
    const data = old as GenerationsCache
    return { ...data, data: { ...data.data, generations: updater(data.data.generations) } }
  })
}

function getDefaultAlbumName() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} 촬영`
}

export function GenerateModal({ open, onClose, preselectedAlbumId }: GenerateModalProps) {
  const form = useGenerationForm('GROOM')
  const { grouped, isLoading: blocksLoading } = usePromptBlocksGrouped()
  const { albums } = useAlbums()
  const addToast = useUIStore((s) => s.addToast)
  const sse = useSSEGenerate<SSEEvent>()
  const queryClient = useQueryClient()
  const prevImageCountRef = useRef(0)
  const generationStartedAtRef = useRef('')

  const [count, setCount] = useState(1)
  const [albumMode, setAlbumMode] = useState<'existing' | 'new'>('new')
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)
  const [albumName, setAlbumName] = useState(getDefaultAlbumName)

  // 모달 열릴 때 preselectedAlbumId 반영
  useEffect(() => {
    if (open && preselectedAlbumId) {
      setAlbumMode('existing')
      setSelectedAlbumId(preselectedAlbumId)
    }
  }, [open, preselectedAlbumId])

  // 모달 닫힐 때 폼 + SSE 상태 리셋
  useEffect(() => {
    if (!open) {
      form.reset()
      setCount(1)
      setAlbumMode(preselectedAlbumId ? 'existing' : 'new')
      setSelectedAlbumId(preselectedAlbumId ?? null)
      setAlbumName(getDefaultAlbumName())
      sse.reset()
    }
  }, [open, form.reset, sse.reset, preselectedAlbumId])

  // 언마운트 시 SSE 정리
  useEffect(() => {
    return () => {
      sse.abort()
    }
  }, [sse.abort])

  // SSE 이벤트 변화 시 최근 사진 캐시 갱신
  useEffect(() => {
    const imageEvents = sse.events.filter((e): e is SSEEvent & { type: 'image' } => e.type === 'image')
    const doneEvent = sse.events.find((e) => e.type === 'done') as (SSEEvent & { type: 'done' }) | undefined

    if (doneEvent) {
      updateGenerationsCache(queryClient, (gens) => gens.filter((g) => g.id !== 'temp-generating'))
      prevImageCountRef.current = 0
      generationStartedAtRef.current = ''
      // 앨범 목록 갱신
      queryClient.invalidateQueries({ queryKey: ['/api/ai/albums'] })
      return
    }

    if (imageEvents.length > 0 && imageEvents.length !== prevImageCountRef.current) {
      prevImageCountRef.current = imageEvents.length
      const urls = imageEvents.map((e) => e.url)
      updateGenerationsCache(queryClient, (gens) => {
        const existing = gens.filter((g) => g.id !== 'temp-generating')
        const tempEntry = { id: 'temp-generating', generatedUrls: urls, style: null, createdAt: generationStartedAtRef.current }
        return [tempEntry, ...existing]
      })
    }
  }, [sse.events, queryClient])

  const canGenerate = form.isFormValid && sse.phase !== 'generating'

  const handleSubmit = useCallback(() => {
    if (!canGenerate) return

    const imageUrls = form.getSelectedImageUrls()
    generationStartedAtRef.current = new Date().toISOString()
    sse.start({
      url: '/api/ai/studio-generate',
      buildBody: () =>
        JSON.stringify({
          imageUrls,
          role: form.role,
          count,
          promptBlocks: form.blockSelection as PromptBlockSelection,
          albumId: albumMode === 'existing' ? selectedAlbumId : undefined,
          albumName: albumMode === 'new' ? albumName.trim() || getDefaultAlbumName() : undefined,
        }),
    })
  }, [canGenerate, form, count, sse, albumMode, selectedAlbumId, albumName])

  const handleClose = useCallback(() => {
    if (sse.phase === 'generating') {
      const imageEvents = sse.events.filter((e) => e.type === 'image')
      useBackgroundGenStore.getState().start({
        count,
        completedSoFar: imageEvents.length,
        startedAt: generationStartedAtRef.current,
        albumId: albumMode === 'existing' ? (selectedAlbumId ?? undefined) : undefined,
      })
      sse.abort()
      addToast({ type: 'info', message: '생성이 백그라운드에서 계속됩니다' })
    }
    queryClient.invalidateQueries({ queryKey: GENERATIONS_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: ['/api/ai/albums'] })
    onClose()
  }, [sse.phase, sse.events, sse.abort, onClose, addToast, queryClient, count, albumMode, selectedAlbumId])

  // GenerationProgress용 이벤트 변환
  const progressEvents = useMemo(() =>
    sse.events.map((e) => {
      if (e.type === 'done') {
        return { type: 'done' as const, generatedUrls: e.urls, remainingCredits: e.remainingCredits, failureCount: e.failureCount, creditsUsed: e.creditsUsed }
      }
      return e
    }),
    [sse.events],
  )

  const isSSEActive = sse.phase === 'generating' || sse.phase === 'done' || sse.phase === 'error'

  const handleAlbumSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value
    if (val === '__new__') {
      setAlbumMode('new')
      setSelectedAlbumId(null)
    } else {
      setAlbumMode('existing')
      setSelectedAlbumId(val)
    }
  }, [])

  return (
    <Modal open={open} onClose={handleClose} title="사진 생성" size="lg">
      {isSSEActive ? (
        <div>
          <GenerationProgress phase={sse.phase} events={progressEvents} error={sse.error} totalImages={count} />
          {(sse.phase === 'done' || sse.phase === 'error') && (
            <div className={styles.footer}>
              <Button variant={sse.phase === 'done' ? 'primary' : 'secondary'} onClick={handleClose} style={{ flex: 1 }}>
                {sse.phase === 'done' ? '확인' : '닫기'}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.form}>
          <div className={styles.section}>
            <span className={styles.sectionTitle}>촬영 선택</span>
            <select
              className={styles.countInput}
              style={{ width: '100%', textAlign: 'left' }}
              value={albumMode === 'new' ? '__new__' : (selectedAlbumId ?? '')}
              onChange={handleAlbumSelectChange}
            >
              <option value="__new__">새 촬영 만들기</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {albumMode === 'new' && (
              <Input
                value={albumName}
                onChange={(e) => setAlbumName(e.target.value)}
                placeholder="촬영 이름 (예: 야외 스냅)"
                style={{ marginTop: 'var(--space-2)' }}
              />
            )}
          </div>

          <div className={styles.section}>
            <span className={styles.sectionTitle}>역할 선택</span>
            <RoleSelector
              selected={form.role}
              onChange={form.setRole}
              hasGroom={form.hasGroom}
              hasBride={form.hasBride}
            />
          </div>

          <div className={styles.section}>
            <span className={styles.sectionTitle}>참조 사진 선택</span>
            <ReferencePhotoSelector
              role={form.role}
              groomPhotos={form.groomPhotos}
              bridePhotos={form.bridePhotos}
              selectedGroomId={form.selectedGroomId}
              selectedBrideId={form.selectedBrideId}
              onSelectGroom={form.setSelectedGroomId}
              onSelectBride={form.setSelectedBrideId}
              onUpload={form.upload}
              uploading={form.uploading}
            />
          </div>

          <div className={styles.section}>
            <span className={styles.sectionTitle}>컨셉 선택</span>
            <ConceptSelector
              grouped={grouped}
              selection={form.blockSelection}
              onChange={form.setBlockSelection}
              isLoading={blocksLoading}
            />
          </div>

          <div className={styles.section}>
            <span className={styles.sectionTitle}>생성 개수</span>
            <div className={styles.countRow}>
              <input
                type="range"
                min={1}
                max={10}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className={styles.rangeSlider}
                style={{ flex: 1 }}
              />
              <span className={styles.countInput} style={{ width: 48, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {count}장
              </span>
            </div>
            <span className={styles.creditInfo}>
              크레딧 {count}개 사용
            </span>
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canGenerate}
              style={{ flex: 1 }}
            >
              생성하기
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
